import { useAuthContext } from '@/auth/useAuthContext';
import { Modal } from '@/components/Modal';
import {
  ApiError,
  type TeamInvitation,
  createInvitation,
  getTeam,
  getTeamRoles,
  listInvitations,
  removeTeamMember,
  resendInvitation,
  revokeInvitation,
  updateTeamMember,
} from '@/lib/api';
import type { Role, TeamMember, UpdateMembershipInput } from '@/lib/types';
import { cn } from '@/lib/utils';
import { Mail, Search, UserPlus, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatJoinDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function statusPill(status: TeamMember['status']) {
  const map: Record<TeamMember['status'], { label: string; classes: string }> = {
    active: { label: 'Active', classes: 'bg-success/10 text-success' },
    invited: { label: 'Invited', classes: 'bg-primary/10 text-primary' },
    suspended: { label: 'Suspended', classes: 'bg-warning/10 text-warning' },
    removed: { label: 'Removed', classes: 'bg-secondary text-muted-foreground' },
  };
  const { label, classes } = map[status] ?? map.active;
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium leading-none ring-hairline',
        classes,
      )}
    >
      {label}
    </span>
  );
}

// Pending invitations are server-side now (TeamInvitation in @/lib/api).

// ---------------------------------------------------------------------------
// Invite modal
// ---------------------------------------------------------------------------

interface InviteModalProps {
  roles: Role[];
  onClose: () => void;
  onInvited: (invite: TeamInvitation) => void;
}

const INVITABLE_SLUGS = ['manager', 'staff', 'groomer', 'front_desk'];

function InviteModal({ roles, onClose, onInvited }: InviteModalProps) {
  const invitableRoles = roles.filter((r) => INVITABLE_SLUGS.includes(r.slug) || !r.isBuiltin);
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState(invitableRoles[0]?.id ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    setError(null);
    const trimmed = email.trim();
    if (!trimmed || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
      setError('A valid email is required.');
      return;
    }
    const role = invitableRoles.find((r) => r.id === roleId);
    if (!role) {
      setError('Select a role.');
      return;
    }
    setBusy(true);
    try {
      const invitation = await createInvitation({ email: trimmed, roleId });
      onInvited(invitation);
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'already_invited') {
        setError('An invitation has already been sent to this email.');
      } else {
        setError(err instanceof ApiError ? err.code : 'Could not send invitation.');
      }
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} aria-label="Invite member" maxWidth="max-w-md">
      <form
        className="p-6 space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        <div className="flex items-start justify-between">
          <h2 className="font-heading text-lg font-medium">Invite member</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground press"
            aria-label="Close"
          >
            <X className="size-4" strokeWidth={1.75} />
          </button>
        </div>

        <p className="text-sm text-muted-foreground">
          We&apos;ll email an invitation to this address. They&apos;ll click through to accept and
          set up their account.
        </p>

        <div>
          <label
            htmlFor="invite-email"
            className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            Email
          </label>
          <input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError(null);
            }}
            placeholder="staff@example.com"
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>

        <div>
          <label
            htmlFor="invite-role"
            className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            Role
          </label>
          <select
            id="invite-role"
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            {invitableRoles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>

        {error !== null && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground press"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press disabled:opacity-50"
          >
            {busy ? 'Sending…' : 'Send invitation'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Remove confirm modal
// ---------------------------------------------------------------------------

interface RemoveModalProps {
  member: TeamMember;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

function RemoveModal({ member, onClose, onConfirm }: RemoveModalProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
    } catch (err: unknown) {
      const msg = err instanceof ApiError ? err.code : 'Something went wrong';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} aria-label="Confirm remove member" maxWidth="max-w-sm">
      <div className="p-6 space-y-4">
        <h2 className="font-heading text-lg font-medium">Remove member?</h2>
        <p className="text-sm text-muted-foreground">
          This will mark {member.userName ?? member.userEmail} as removed. The record is preserved
          for the audit trail.
        </p>
        {error !== null && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground press"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={busy}
            className="rounded-md bg-destructive px-4 py-2 text-sm font-medium text-white press disabled:opacity-50"
          >
            {busy ? 'Removing…' : 'Remove member'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function TeamSettings() {
  const { state } = useAuthContext();
  const me = state.me;

  const [team, setTeam] = useState<TeamMember[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pendingInvites, setPendingInvites] = useState<TeamInvitation[]>([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [removingMember, setRemovingMember] = useState<TeamMember | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Filter / search state
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<TeamMember['status'] | 'all'>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [teamData, rolesData, invitesData] = await Promise.all([
        getTeam(),
        getTeamRoles(),
        listInvitations().catch(() => ({ invitations: [] })),
      ]);
      setTeam(teamData);
      setRoles(rolesData);
      setPendingInvites(invitesData.invitations);
    } catch (err: unknown) {
      const msg = err instanceof ApiError ? err.code : 'Failed to load team';
      setLoadError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleRoleChange(member: TeamMember, newRoleId: string) {
    if (newRoleId === member.roleId) return;
    const patch: UpdateMembershipInput = { roleId: newRoleId };
    try {
      const updated = await updateTeamMember(member.id, patch);
      setTeam((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
    } catch (err: unknown) {
      const msg = err instanceof ApiError ? err.code : 'Update failed';
      setLoadError(msg);
    }
  }

  async function handleRemove(member: TeamMember) {
    await removeTeamMember(member.id);
    setRemovingMember(null);
    await load();
  }

  function handleInvited(invitation: TeamInvitation) {
    setPendingInvites((prev) => [invitation, ...prev]);
    setToast(`Invitation sent to ${invitation.email}`);
    setTimeout(() => setToast(null), 3000);
  }

  async function handleResend(id: string, email: string) {
    try {
      await resendInvitation(id);
      setToast(`Re-sent invitation to ${email}`);
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.code : 'Could not resend invitation');
    }
  }

  async function handleRevoke(id: string) {
    try {
      await revokeInvitation(id);
      setPendingInvites((prev) => prev.filter((i) => i.id !== id));
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.code : 'Could not revoke invitation');
    }
  }

  const myUserId = me?.user.id;
  const ownerRoleSlug = 'owner';

  // Derived filtered team list
  const filteredTeam = team.filter((m) => {
    if (statusFilter !== 'all' && m.status !== statusFilter) return false;
    if (roleFilter !== 'all' && m.roleSlug !== roleFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      const name = (m.userName ?? '').toLowerCase();
      const email = (m.userEmail ?? '').toLowerCase();
      if (!name.includes(q) && !email.includes(q)) return false;
    }
    return true;
  });

  const builtInRoleSlugs = ['owner', 'manager', 'staff', 'groomer', 'front_desk'];
  const filterableRoles = roles.filter((r) => builtInRoleSlugs.includes(r.slug));

  if (state.loading || loading) {
    return (
      <div className="space-y-3">
        <div className="h-8 w-40 animate-pulse rounded bg-secondary" />
        <div className="h-4 w-full animate-pulse rounded bg-secondary" />
        <div className="h-4 w-2/3 animate-pulse rounded bg-secondary" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between gap-4">
        <h1 className="font-heading text-2xl font-medium tracking-tight">Team</h1>
        <button
          type="button"
          onClick={() => setShowInviteModal(true)}
          className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press"
        >
          <UserPlus className="size-4" strokeWidth={1.75} />
          Invite member
        </button>
      </header>

      {loadError !== null && <p className="text-sm text-destructive">{loadError}</p>}

      {/* Search + filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search
            className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground"
            strokeWidth={1.75}
          />
          <input
            type="search"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 w-56 rounded-md border border-border bg-surface pl-8 pr-3 text-sm outline-none focus:ring-2 focus:ring-primary/30"
            aria-label="Search members"
          />
        </div>
        {/* Status filter pills */}
        {(['all', 'active', 'invited', 'suspended'] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            className={cn(
              'rounded-full px-3 py-1 text-xs font-medium transition-colors press',
              statusFilter === s
                ? 'bg-primary text-primary-foreground'
                : 'border border-border bg-surface text-muted-foreground hover:bg-secondary',
            )}
          >
            {s === 'all' ? 'All status' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
        {/* Role filter */}
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="h-8 rounded-md border border-border bg-surface px-2 text-xs outline-none focus:ring-2 focus:ring-primary/30"
          aria-label="Members: filter by position"
        >
          <option value="all">All roles</option>
          {filterableRoles.map((r) => (
            <option key={r.slug} value={r.slug}>
              {r.name}
            </option>
          ))}
        </select>
        {(search || statusFilter !== 'all' || roleFilter !== 'all') && (
          <button
            type="button"
            onClick={() => {
              setSearch('');
              setStatusFilter('all');
              setRoleFilter('all');
            }}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground press"
          >
            <X className="size-3" strokeWidth={2} />
            Clear filters
          </button>
        )}
      </div>

      {/* Team table */}
      <section className="rounded-2xl bg-surface ring-1 ring-border elevate overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[640px] w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/40">
                <th className="px-5 py-3 text-left text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Member
                </th>
                <th className="px-5 py-3 text-left text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Role
                </th>
                <th className="px-5 py-3 text-left text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground hidden md:table-cell">
                  Facility scope
                </th>
                <th className="px-5 py-3 text-left text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground hidden lg:table-cell">
                  Joined
                </th>
                <th className="px-5 py-3 text-left text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Status
                </th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredTeam.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-10 text-center text-sm text-muted-foreground">
                    {team.length === 0
                      ? 'No team members yet.'
                      : 'No members match the current filters.'}
                  </td>
                </tr>
              )}
              {filteredTeam.map((member) => {
                const isMe = member.userId === myUserId;
                const isOwner = member.roleSlug === ownerRoleSlug;
                const canEdit = !(isMe && isOwner);
                const isRemoved = member.status === 'removed';

                return (
                  <tr key={member.id} className={cn(isRemoved && 'opacity-50')}>
                    <td className="px-5 py-3">
                      <div className="font-medium leading-snug">{member.userName ?? '—'}</div>
                      <div className="text-xs text-muted-foreground">{member.userEmail}</div>
                    </td>
                    <td className="px-5 py-3">
                      {canEdit && !isRemoved ? (
                        <select
                          aria-label={`Role for ${member.userName ?? member.userEmail}`}
                          value={member.roleId}
                          onChange={(e) => void handleRoleChange(member, e.target.value)}
                          className="rounded-md border border-border bg-surface px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                        >
                          {roles.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-sm">{member.roleName}</span>
                      )}
                    </td>
                    <td className="px-5 py-3 hidden md:table-cell">
                      {member.facilityScopeIds === null ? (
                        <span className="text-xs text-muted-foreground">All facilities</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {member.facilityScopeIds.map((fid) => (
                            <span
                              key={fid}
                              className="num inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium leading-none ring-hairline"
                            >
                              {fid}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 hidden lg:table-cell">
                      <span className="num text-xs text-muted-foreground">
                        {formatJoinDate(member.acceptedAt)}
                      </span>
                    </td>
                    <td className="px-5 py-3">{statusPill(member.status)}</td>
                    <td className="px-5 py-3 text-right">
                      {canEdit && !isRemoved && (
                        <button
                          type="button"
                          onClick={() => setRemovingMember(member)}
                          className="text-xs text-destructive hover:underline press"
                          aria-label={`Remove ${member.userName ?? member.userEmail}`}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Pending invitations */}
      <section>
        <h2 className="mb-3 font-heading text-lg font-medium">Pending invitations</h2>

        {pendingInvites.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No outstanding invitations. Use &ldquo;Invite member&rdquo; to send one.
          </p>
        ) : (
          <ul className="space-y-2">
            {pendingInvites.map((invite) => (
              <li
                key={invite.id}
                className="flex items-center justify-between gap-3 rounded-lg bg-surface px-4 py-3 ring-1 ring-border"
              >
                <div className="flex items-center gap-2">
                  <Mail className="size-4 text-muted-foreground" strokeWidth={1.75} />
                  <span className="text-sm font-medium">{invite.email}</span>
                  <span className="text-xs text-muted-foreground">{invite.roleName}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => void handleResend(invite.id, invite.email)}
                    className="text-xs text-primary hover:underline press"
                  >
                    Resend
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleRevoke(invite.id)}
                    aria-label={`Revoke invitation for ${invite.email}`}
                    className="text-xs text-destructive hover:underline press"
                  >
                    Revoke
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-6 right-6 z-50 rounded-lg bg-foreground px-4 py-2 text-sm font-medium text-background shadow-lg"
        >
          {toast}
        </div>
      )}

      {/* Modals */}
      {showInviteModal && (
        <InviteModal
          roles={roles}
          onClose={() => setShowInviteModal(false)}
          onInvited={handleInvited}
        />
      )}
      {removingMember !== null && (
        <RemoveModal
          member={removingMember}
          onClose={() => setRemovingMember(null)}
          onConfirm={() => handleRemove(removingMember)}
        />
      )}
    </div>
  );
}
